import { Alert, Box, Stack, Typography, useTheme } from '@mui/material';
import {
  GridChartsIntegrationContextProvider,
  GridChartsRendererProxy,
  GridTreeDataGroupingCell,
  isAutogeneratedRow,
  type GridChartsPanelProps,
  type DataGridPremiumProps,
  type GridGroupNode,
  type GridRowParams,
  type GridSortModel,
  type GridPivotModel,
} from '@mui/x-data-grid-premium';
import {
  ChartsRenderer,
  configurationOptions,
  type ChartsRendererProps,
} from '@mui/x-charts-premium/ChartsRenderer';
import { useCallback, useMemo, useState } from 'react';
import type { CustomField } from '../GeneratedClient';
import type {
  ClientAssignmentRoleV2,
  ClientBrowserRowV2,
} from './useClientsBrowserViewModel';
import {
  clientsInternalSortVisibility,
  clientsGridSortSlotProps,
} from './clientsGridSorting';
import {
  buildClientsColumns,
  isInternalClientsColumn,
  isOptionalClientsColumn,
} from './clientsGridColumns';
import { ClientsChartsPanel } from './ClientsChartsPanel';
import { usePersistedGridFilterModel } from '../Hooks/usePersistedGridFilterModel';
import { PersistedFilterDataGridPremium } from '../Generic/PersistedFilterDataGridPremium';
import { usePersistedGridColumnPreferences } from '../Hooks/usePersistedGridColumnPreferences';
import { v2DataGridFocusStyles } from '../Families/v2DataGridStyles';
import { AgeText } from '../Families/AgeText';
import { simplify } from '../Utilities/stringUtils';
import { ClientFamilyCellV2 } from './ClientFamilyCellV2';
import { clientsPivotRows, clientsPivotScope } from './clientsPivotScope';

type ClientsDataGridV2Props = {
  rows: ClientBrowserRowV2[];
  assignmentRoles: ClientAssignmentRoleV2[];
  familyCustomFields: CustomField[];
  caseCustomFields: CustomField[];
  adultCustomFields: CustomField[];
  childCustomFields: CustomField[];
  countyOptions: string[];
  loading?: boolean;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  onRowClick: (row: ClientBrowserRowV2) => void;
};

const clientsGridSlots = {
  noRowsOverlay: ClientsEmptyState,
};
const clientsGridSlotProps = {
  ...clientsGridSortSlotProps,
  columnsManagement: {
    getTogglableColumns: (
      columns: Parameters<
        typeof clientsGridSortSlotProps.columnsManagement.getTogglableColumns
      >[0]
    ) =>
      clientsGridSortSlotProps.columnsManagement
        .getTogglableColumns(columns)
        .filter(
          (field) => field !== 'family' && !isInternalClientsColumn(field)
        ),
  },
  filterPanel: {
    filterFormProps: {
      valueInputProps: { sx: { width: 280 } },
    },
  },
  chartsPanel: {
    schema: {
      bar: configurationOptions.bar,
      column: configurationOptions.column,
      pie: configurationOptions.pie,
    },
  },
};
function clientsGridSx(theme: Parameters<typeof v2DataGridFocusStyles>[0]) {
  return {
    ...v2DataGridFocusStyles(theme),
    '& .MuiDataGrid-columnHeaderDraggableContainer[draggable="true"]': {
      cursor: 'grab',
    },
    '& .MuiDataGrid-row': { cursor: 'pointer', minHeight: 56 },
    '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex', py: 1 },
  };
}
function getClientsRowHeight() {
  return 'auto' as const;
}
function getEstimatedClientsRowHeight() {
  return 72;
}
const getClientsTreePath = (row: ClientBrowserRowV2) => row.treePath;
const initialClientsPivotModel: GridPivotModel = {
  rows: [],
  columns: [],
  values: [{ field: 'reportCount', aggFunc: 'sum' }],
};
const pivotScopeLabels = {
  family: 'families',
  individual: 'adults and children',
  adult: 'adults',
  child: 'children',
};

function ClientsEmptyState() {
  return (
    <Stack
      spacing={0.5}
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 180,
        px: 2,
        textAlign: 'center',
      }}
    >
      <Typography className="ph-unmask" variant="subtitle1">
        No client families found.
      </Typography>
      <Typography className="ph-unmask" color="text.secondary" variant="body2">
        Client families will appear here when they are available.
      </Typography>
    </Stack>
  );
}

export function ClientsDataGridV2({
  rows,
  assignmentRoles,
  familyCustomFields,
  caseCustomFields,
  adultCustomFields,
  childCustomFields,
  countyOptions,
  loading = false,
  sortModel,
  onSortModelChange,
  onRowClick,
}: ClientsDataGridV2Props) {
  const theme = useTheme();
  const [pivotActive, setPivotActive] = useState(false);
  const [pivotPanelOpen, setPivotPanelOpen] = useState(false);
  const [pivotModel, setPivotModel] = useState<GridPivotModel>(
    initialClientsPivotModel
  );
  const slots = useMemo(
    () => ({
      ...clientsGridSlots,
      chartsPanel: (props: GridChartsPanelProps) => (
        <ClientsChartsPanel {...props} pivotActive={pivotActive} />
      ),
    }),
    [pivotActive]
  );
  const renderChart = useCallback(
    (props: ChartsRendererProps) =>
      pivotActive && props.chartType ? (
        <Box sx={{ width: '100%', minWidth: 0, overflow: 'auto' }}>
          <ChartsRenderer {...props} />
        </Box>
      ) : null,
    [pivotActive]
  );
  const columns = useMemo(
    () =>
      buildClientsColumns(
        rows,
        assignmentRoles,
        familyCustomFields,
        caseCustomFields,
        adultCustomFields,
        childCustomFields,
        countyOptions
      ),
    [
      rows,
      assignmentRoles,
      familyCustomFields,
      caseCustomFields,
      adultCustomFields,
      childCustomFields,
      countyOptions,
    ]
  );
  const defaultColumnVisibilityModel = useMemo(
    () =>
      Object.fromEntries(
        columns
          .filter((column) => isOptionalClientsColumn(column.field))
          .map((column) => [column.field, false])
      ),
    [columns]
  );
  const {
    apiRef,
    columnVisibilityModel,
    onColumnOrderChange,
    onColumnVisibilityModelChange,
  } = usePersistedGridColumnPreferences({
    columns,
    defaultColumnVisibilityModel,
    namespace: 'clients',
  });
  const treeRows = useMemo(
    () =>
      new Map(rows.map((row) => [row.treePath[row.treePath.length - 1], row])),
    [rows]
  );
  const groupingColDef = useMemo<
    DataGridPremiumProps<ClientBrowserRowV2>['groupingColDef']
  >(
    () => ({
      headerName: 'Family',
      hideable: false,
      minWidth: 240,
      flex: 1,
      sortable: true,
      sortComparator: (firstValue, secondValue) => {
        const first = treeRows.get(String(firstValue));
        const second = treeRows.get(String(secondValue));
        const firstName =
          first?.rowKind === 'family' ? first.family : (first?.personName ?? '');
        const secondName =
          second?.rowKind === 'family'
            ? second.family
            : (second?.personName ?? '');
        return (
          firstName.localeCompare(secondName, undefined, {
            sensitivity: 'base',
          }) || String(firstValue).localeCompare(String(secondValue))
        );
      },
      valueFormatter: (value) => {
        const row = treeRows.get(String(value));
        return row?.rowKind === 'family' ? row.family : (row?.personName ?? '');
      },
      getApplyQuickFilterFn: (value) => {
        const query = simplify(String(value));
        return (cell) => {
          const row = treeRows.get(String(cell));
          if (!row) return false;
          const searchable =
            row.rowKind === 'family'
              ? [
                  row.family,
                  row.memberNames,
                  row.primaryContactName,
                  row.phoneNumber,
                ]
              : [row.personName];
          return simplify(searchable.filter(Boolean).join(' ')).includes(query);
        };
      },
      renderCell: (params) => (
        <GridTreeDataGroupingCell
          {...params}
          hideDescendantCount
          rowNode={params.rowNode as GridGroupNode}
          formattedValue={
            params.row.rowKind === 'family' ? (
              <ClientFamilyCellV2
                familyName={params.row.family || '-'}
                memberSummaries={params.row.memberSummaries}
              />
            ) : (
              <>
                {params.row.personName}
                {params.row.personAge && (
                  <>
                    {' '}
                    <em>
                      <small>
                        (<AgeText age={params.row.personAge} />)
                      </small>
                    </em>
                  </>
                )}
              </>
            )
          }
        />
      ),
    }),
    [treeRows]
  );
  const { filterModel, filterModelRestored, onFilterModelChange } =
    usePersistedGridFilterModel({
      columns,
      namespace: 'clients',
    });
  const pivotScope = clientsPivotScope(
    pivotModel,
    filterModel.items.map((item) => item.field)
  );
  const pivotRows = useMemo(
    () => clientsPivotRows(rows, pivotScope),
    [rows, pivotScope]
  );
  const visibleColumns = useMemo(
    () => ({
      ...columnVisibilityModel,
      ...clientsInternalSortVisibility,
      family: pivotActive ? (columnVisibilityModel.family ?? true) : false,
    }),
    [columnVisibilityModel, pivotActive]
  );
  const handleRowClick = useCallback(
    ({ row }: GridRowParams<ClientBrowserRowV2>, event: React.MouseEvent) => {
      if ((event.target as HTMLElement).closest('button')) return;
      if (!pivotActive && !isAutogeneratedRow(row) && row.familyId)
        onRowClick(row);
    },
    [onRowClick, pivotActive]
  );
  return (
    <GridChartsIntegrationContextProvider>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          minHeight: 0,
          minWidth: 0,
          overflowY: 'auto',
        }}
      >
        {pivotActive && pivotScope !== 'mixed' && (
          <Typography className="ph-unmask" variant="body2" sx={{ p: 1 }}>
            Counting {pivotScopeLabels[pivotScope]}
          </Typography>
        )}
        {pivotActive && pivotScope === 'mixed' && (
          <Alert severity="info" sx={{ m: 1 }}>
            Choose adult or child custom fields in one pivot to count people.
          </Alert>
        )}
        <Box
          sx={{ height: '100%', minHeight: 'min(360px, 100%)', minWidth: 0 }}
        >
          <PersistedFilterDataGridPremium
            key={pivotActive ? 'clients-pivot-grid' : 'clients-tree-grid'}
            showToolbar
            chartsIntegration
            treeData={!pivotActive}
            getTreeDataPath={getClientsTreePath}
            groupingColDef={pivotActive ? undefined : groupingColDef}
            disableChildrenSorting
            keepColumnPositionIfDraggedOutside
            disableAggregation
            pivotActive={pivotActive}
            onPivotActiveChange={setPivotActive}
            pivotPanelOpen={pivotPanelOpen}
            onPivotPanelOpenChange={setPivotPanelOpen}
            pivotModel={pivotModel}
            onPivotModelChange={setPivotModel}
            aggregationRowsScope="filtered"
            pagination={false}
            rows={pivotActive ? pivotRows : rows}
            columns={columns}
            sortModel={pivotActive ? undefined : sortModel}
            onSortModelChange={pivotActive ? undefined : onSortModelChange}
            filterModel={filterModel}
            filterModelRestored={filterModelRestored}
            onFilterModelChange={onFilterModelChange}
            filterDebounceMs={200}
            columnVisibilityModel={visibleColumns}
            onColumnVisibilityModelChange={
              pivotActive ? undefined : onColumnVisibilityModelChange
            }
            apiRef={apiRef}
            onColumnOrderChange={pivotActive ? undefined : onColumnOrderChange}
            columnHeaderHeight={42}
            density="comfortable"
            disableRowSelectionOnClick
            getRowHeight={getClientsRowHeight}
            getEstimatedRowHeight={getEstimatedClientsRowHeight}
            loading={loading}
            onRowClick={handleRowClick}
            slots={slots}
            slotProps={clientsGridSlotProps}
            sx={clientsGridSx(theme)}
          />
        </Box>
        <GridChartsRendererProxy
          id="clients-report"
          label="Client count"
          renderer={renderChart}
        />
      </Box>
    </GridChartsIntegrationContextProvider>
  );
}
